import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  ExportDropdown,
  Hours,
  InternalClientTooltip,
  Percent,
  ProjectLink,
  RouteLink,
  Widget,
  YesNoFilter,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  ReportPeriodFilter,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTaskFilters,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

function TimeSummaryByProject({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const projectRoleFilters = useProjectRoleFilters();
  const memberFilters = useMemberFilters();
  const taskFilters = useTaskFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    includeProjectsWithoutTime: false,
    sort: new QuerySort('name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...projectRoleFilters.filters,
    ...taskFilters.filters,
    ...memberFilters.filters,
    ...timeFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        includeProjectsWithoutTime: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        sort: { default: new QuerySort('name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...projectRoleFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        projectRoleFilters,
        memberFilters,
        taskFilters,
        timeFilters,
      ],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      includeProjectsWithoutTime: params.includeProjectsWithoutTime ? 'true' : undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...projectRoleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, taskFilters, projectRoleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter value={params.period} onChange={({ value }) => handleApplyFilters({ period: value })} />

        <YesNoFilter
          icon="filter"
          placeholder="Include projects without time"
          value={params.includeProjectsWithoutTime ? 'yes' : 'no'}
          onChange={({ target: { value } }) => handleApplyFilters({ includeProjectsWithoutTime: value === 'yes' })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />

        <TaskFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const auth = useAuth();

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <>
      <Report.Summary>
        <Widget>
          <Meter>
            <Meter.Value
              label="Client Billable"
              value={report.totals.billable}
              color={colors.success}
              tooltip={
                <>
                  <Hours value={report.totals.billable} /> hours (
                  <Percent value={report.totals.billable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Client Non-Billable"
              value={report.totals.nonBillable}
              color={colors.danger}
              tooltip={
                <>
                  <Hours value={report.totals.nonBillable} /> hours (
                  <Percent value={report.totals.nonBillable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Internal"
              value={report.totals.internal}
              color={colors.warning}
              tooltip={
                <>
                  <Hours value={report.totals.internal} /> hours (
                  <Percent value={report.totals.internal / report.totals.total} />)
                </>
              }
            />
          </Meter>

          <Metrics>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'billable' })}>
                <Metric.Value>
                  <Hours value={report.totals.billable} />
                </Metric.Value>
                <Metric.Label>Client Billable</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'non_billable' })}>
                <Metric.Value>
                  <Hours value={report.totals.nonBillable} />
                </Metric.Value>
                <Metric.Label>Client Non-Billable</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'internal' })}>
                <Metric.Value>
                  <Hours value={report.totals.internal} />
                </Metric.Value>
                <Metric.Label>Internal</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: ['billable', 'non_billable', 'internal'] })}>
                <Metric.Value>
                  <Hours value={report.totals.total} />
                </Metric.Value>
                <Metric.Label>Total</Metric.Label>
              </RouteLink>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="name" onSort={onSort} sort={params.sort}>
              Project/Client
            </Table.Column>
            <Table.Column name="billable" width="9rem" align="right" onSort={onSort} sort={params.sort}>
              Client
              <br />
              Billable
            </Table.Column>
            <Table.Column name="nonBillable" width="9rem" align="right" onSort={onSort} sort={params.sort}>
              Client
              <br />
              Non-Billable
            </Table.Column>
            <Table.Column name="internal" width="9rem" align="right" onSort={onSort} sort={params.sort}>
              Internal
            </Table.Column>
            <Table.Column name="total" width="9rem" align="right" onSort={onSort} sort={params.sort}>
              Total
            </Table.Column>
            <Table.Column
              isVisible={auth.projects.viewBudgetHours}
              name="budgetHours"
              width="9rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Budget
            </Table.Column>
            <Table.Column
              isVisible={auth.projects.viewBudgetHours}
              name="budgetHoursLeft"
              width="9rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Budget Left
            </Table.Column>
            <Table.Column
              isVisible={auth.projects.viewBudgetHours}
              name="percentLeft"
              width="9rem"
              align="right"
              onSort={onSort}
              sort={params.sort}>
              Percent
              <br />
              Left
            </Table.Column>
          </Table.Header>
          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((project) => {
              return (
                <Table.Row key={project.id}>
                  <Table.Cell>
                    <ProjectInfo>
                      <p>
                        <ProjectLink project={project} />
                      </p>
                      <small>
                        <ClientLink client={project.client} />
                        {project.client.isInternal && <InternalClientTooltip />}
                      </small>
                    </ProjectInfo>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeBillableTypeId: 'billable', projectId: project.id })}>
                      <Hours value={project.billable} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeBillableTypeId: 'non_billable', projectId: project.id })}>
                      <Hours value={project.nonBillable} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeBillableTypeId: 'internal', projectId: project.id })}>
                      <Hours value={project.internal} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ projectId: project.id })}>
                      <Hours value={project.total} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <Hours value={project.budgetHours} />
                  </Table.Cell>
                  <Table.Cell>
                    <Hours value={project.budgetHoursLeft} />
                  </Table.Cell>
                  <Table.Cell>
                    <Percent value={project.percentLeft} />
                  </Table.Cell>
                </Table.Row>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: 'billable' })}>
                  <Hours value={report.totals.billable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: 'non_billable' })}>
                  <Hours value={report.totals.nonBillable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: 'internal' })}>
                  <Hours value={report.totals.internal} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: ['billable', 'non_billable', 'internal'] })}>
                  <Hours value={report.totals.total} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.budgetHours} />
              </Table.Cell>
              <Table.Cell>
                <Hours value={report.totals.budgetHoursLeft} />
              </Table.Cell>
              <Table.Cell>
                <Percent value={report.totals.percentLeft} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Project"
            isLoading={query.status !== 'ready'}
            partialResults={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        placeholder="Include projects without time"
        value={filters.includeProjectsWithoutTime ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeProjectsWithoutTime: value === 'yes' })}
      />

      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

export default TimeSummaryByProject;
